import React from 'react';
import classNames from 'classnames';
import {
  UseFormRegister,
  Controller,
  useFormContext,
  FieldError,
} from 'react-hook-form';
import { get } from 'lodash';
import { FormControl } from './FormControl';
import { Testable } from '../types';

interface SelectOption {
  label: string;
  value: string;
}

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  options: Array<SelectOption>;
  placeholder?: string;
}

export function Select(props: SelectProps) {
  return (
    <select
      {...props}
      className={classNames(
        'mt-1 w-full pl-3 pr-10 block py-3 text-base border-gray-300 focus:outline-none focus:ring-blue focus:border-blue sm:text-sm rounded-md',
        props.disabled ? 'bg-gray-100' : 'shadow-sm',
        'border-gray-300',
        props.className,
      )}
    >
      {!!props.placeholder && (
        <option value="" hidden>
          {props.placeholder}
        </option>
      )}
      {props.options.map(({ label, value }) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </select>
  );
}

export interface FormSelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  name: string;
  options: { label: string; value: string }[];
  label?: string;
  register?: UseFormRegister<any>;
  // `<select>` has no native placeholder attribute, and @types/react 18.3
  // dropped it from the base HTML attributes. Declared explicitly here (as
  // SelectProps already does); it renders as a hidden first <option>.
  placeholder?: string;
}

export function FormSelect({
  options,
  label,
  placeholder = 'Select',
  name,
  ...selectProps
}: Testable<FormSelectProps>) {
  const {
    control,
    formState: { errors, isSubmitting },
  } = useFormContext();
  const error = get(errors, name);

  return (
    <FormControl error={error as FieldError} label={label} name={name}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            {...selectProps}
            {...field}
            onChange={(e) => {
              selectProps.onChange?.(e);
              field.onChange?.(e);
            }}
            placeholder={placeholder}
            options={options}
            className={classNames(
              isSubmitting && 'bg-gray-100',
              error && 'border-red-600',
            )}
          />
        )}
      />
    </FormControl>
  );
}
